import { Link, NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  CheckSquare,
  ScanSearch,
  Package,
  Settings,
  Home,
  ShieldAlert,
  ArrowLeft,
  Globe,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  ScrollText,
  Table2,
  Target,
  Video,
  Undo2,
  Radio,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ChannelPlatform } from "@/lib/types";
import { useAuth } from "@/auth/AuthContext";
import { useAccess } from "@/auth/AccessContext";
import { cn } from "@/lib/cn";
import { IconBadge } from "@/components/IconBadge";
import type { IconTint } from "@/components/IconBadge";
import { Wordmark } from "@/components/Wordmark";
import { BrandSelector } from "@/components/BrandSelector";
import { OrgPicker } from "@/components/OrgPicker";
import { BrandAvatar } from "@/components/BrandAvatar";
import { RegionPicker } from "@/components/RegionPicker";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { UpdateNotice } from "@/components/UpdateNotice";
import { ChatPanel } from "@/components/ChatPanel";
import { useRegion } from "@/lib/useRegion";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** ADR-0008: emoji-in-tinted-square. IconBadge renders this instead of
   *  the lucide glyph when present; the glyph remains as fallback. */
  emoji?: string;
  tint?: IconTint;
  /** Optional: only shown when at least one of these channels is connected. */
  channels?: ChannelPlatform[];
  /** Optional: gates by org role hierarchy. */
  minRole?: "creative" | "viewer" | "analyst" | "admin" | "owner";
  /** Optional: internal-only surface — visible to MarketplacesIQ superadmins only. */
  superAdminOnly?: boolean;
  /** Optional: hide from the nav for EVERYONE (incl. superadmins). Park a surface
   *  that isn't customer-ready; the route/page stays reachable by direct URL. */
  hidden?: boolean;
}

const ROLE_RANK: Record<string, number> = {
  creative: 0,
  viewer: 1,
  analyst: 2,
  admin: 3,
  owner: 4,
};

/**
 * Each group carries a stable `id` (used as the localStorage collapse key)
 * and an optional `heading`. Headingless groups render flat (no toggle).
 *
 * Grouping taxonomy (2026-06-01) — ported the ads-iq "named sections of
 * 2–6 items each" pattern. The old layout had 5 ungrouped top items and a
 * 10-item "Insights" dumping ground; this splits it into 7 tight groups.
 */
type NavGroup = { id: string; heading?: string; items: NavItem[]; requiresSms?: boolean; requiresTikTokShop?: boolean };

// Marketplaces-iq brand navigation — ADR-0008 performance-first regroup.
// Four outcome groups, Performance first because it is also the brand landing
// route: an operator opens on how the brand is trading, discovers demand
// second, optimizes third. The old Workspace/Intelligence split had no
// recorded rationale and neither label told an operator where performance
// lived. Two ADR items are ABSENT deliberately, not forgotten: Discover →
// Search Queries lands with Phase 4 (no route exists yet), and Optimize →
// Changes awaits a change-log page (recent changes render on the Dashboard
// meanwhile).
const BRAND_NAV: NavGroup[] = [
  {
    id: "performance",
    heading: "Performance",
    items: [
      { to: "dashboard", label: "Dashboard", icon: LayoutDashboard, emoji: "📊", tint: "insight" },
      { to: "analytics", label: "Organic", icon: ScanSearch, emoji: "🌱", tint: "scale" },
      { to: "reports", label: "Reports", icon: Table2, emoji: "📄", tint: "insight" },
      { to: "goals", label: "Goals", icon: Target, emoji: "🎯", tint: "goal" },
    ],
  },
  {
    id: "discover",
    heading: "Discover",
    items: [
      { to: "trends", label: "Trends", icon: ScanSearch, emoji: "📈", tint: "insight" },
      { to: "library", label: "Library", icon: Package, emoji: "📦", tint: "subject" },
    ],
  },
  {
    id: "optimize",
    heading: "Optimize",
    items: [
      { to: "optimize", label: "Queue", icon: CheckSquare, emoji: "⚡", tint: "scale" },
      { to: "chat", label: "Advisor", icon: MessageCircle, emoji: "💬", tint: "warm" },
    ],
  },
  {
    id: "setup",
    heading: "Setup",
    items: [
      { to: "amc", label: "Marketing Cloud", icon: Globe, emoji: "☁️", tint: "warm" },
      { to: "audit-log", label: "Audit Log", icon: ScrollText, emoji: "📜", tint: "subject" },
    ],
  },
  {
    id: "tiktok-shop",
    heading: "TikTok Shop",
    requiresTikTokShop: true,
    items: [
      { to: "tiktok/videos",   label: "Videos",   icon: Video,         emoji: "🎬", tint: "tiktok" },
      { to: "tiktok/products", label: "Products", icon: Package,       emoji: "🛍️", tint: "tiktok" },
      { to: "tiktok/returns",  label: "Returns",  icon: Undo2,         emoji: "↩️", tint: "tiktok" },
      { to: "tiktok/live",     label: "Live",     icon: Radio,         emoji: "📡", tint: "tiktok" },
      { to: "tiktok/chat",     label: "Advisor",  icon: MessageCircle, emoji: "💬", tint: "tiktok" },
    ],
  },
];

const linkClasses = (isActive: boolean) =>
  cn(
    "relative flex items-center gap-2.5 mx-2 px-3 py-2.5 mb-0.5 text-sm rounded-md transition-colors",
    isActive
      ? "text-text-primary font-medium bg-accent-muted"
      : "text-text-secondary hover:bg-bg-hover",
  );

// Collapsible-nav-section state, ported from ads-iq. Persists per-section
// collapse to localStorage; auto-collapses any section with >= threshold items
// on first visit. Intelligence is the largest group at 6 items, so no group
// currently hits this and all default expanded — keep it that way when adding a
// nav item, or raise the threshold with it.
//
// Raised 6 -> 7 when Reports was added: that took Intelligence from 5 to 6 and
// silently flipped the whole group to collapsed-by-default for every user with
// no stored preference (new user, new device, incognito), demoting Performance,
// Trends, Marketing Cloud, Audit Log and Goals along with it. The threshold had
// never fired in production, so tripping it was an unintended behaviour change
// rather than a designed one. Note this is invisible to anyone whose browser
// already holds a stored value — it only reproduces on a clean profile.
const COLLAPSE_STORAGE_PREFIX = "marketplacesiq:nav:collapsed:";
const AUTO_COLLAPSE_THRESHOLD = 7;

function useSectionCollapsed(sectionId: string, itemCount: number) {
  const storageKey = COLLAPSE_STORAGE_PREFIX + sectionId;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(storageKey);
    if (stored !== null) return stored === "1";
    return itemCount >= AUTO_COLLAPSE_THRESHOLD;
  });
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* storage disabled / quota — collapse still works in-session */
      }
      return next;
    });
  }, [storageKey]);
  return { collapsed, toggle };
}

function BrandNavLink({ item, slug }: { item: NavItem; slug: string }) {
  return (
    <NavLink
      to={`/brand/${slug}/${item.to}`}
      end
      className={({ isActive }) => linkClasses(isActive)}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
          )}
          <IconBadge icon={item.icon} emoji={item.emoji} tint={item.tint} />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

function BrandNavSection({ group, slug }: { group: NavGroup; slug: string }) {
  const { collapsed, toggle } = useSectionCollapsed(group.id, group.items.length);

  // Defensive: a headingless group renders flat with no toggle.
  if (!group.heading) {
    return (
      <div className="space-y-0.5 mb-3">
        {group.items.map((item) => (
          <BrandNavLink key={item.to} item={item} slug={slug} />
        ))}
      </div>
    );
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        // ADR-0008 header hierarchy fix, adopted from ads-iq V49.1: the old
        // 10px/font-semibold/text-dim made parent labels visually SMALLER and
        // LIGHTER than the nav items they contain. 12.5px bold primary + a
        // border-top separator makes the group boundary legible.
        className="w-full flex items-center gap-1.5 px-4 pt-2.5 pb-1 mt-1 border-t border-border-lighter text-[12.5px] font-bold text-text-primary hover:text-text-secondary transition-colors"
      >
        {collapsed ? (
          <ChevronRight size={11} strokeWidth={2.5} />
        ) : (
          <ChevronDown size={11} strokeWidth={2.5} />
        )}
        <span>{group.heading}</span>
        <span className="ml-auto tabular-nums font-medium text-text-dim/70">
          {group.items.length}
        </span>
      </button>
      {!collapsed && (
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <BrandNavLink key={item.to} item={item} slug={slug} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { slug } = useParams();
  const { me, logout } = useAuth();
  const { isSuperAdmin, isOrgAdmin } = useAccess();

  // retention adaptation: compute `embedded` BEFORE the shell-only queries so
  // it can gate them (it used to be derived later, at the `useLocation`-based
  // computation below, AFTER these queries ran). Derived from the URL directly
  // (not a hook) so hook order stays stable; mirrors ads-iq AppShell, where the
  // shell-only queries are `enabled: !embedded` so the FloatingChat iframe
  // (?embedded=1) doesn't issue the brand/setup fetches the host shell already
  // owns. The embedded early-return short-circuit stays where it is below.
  const embedded =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embedded") === "1";

  const { data: brand } = useQuery({
    queryKey: ["brand", slug],
    queryFn: () => (slug ? api.getBrand(slug) : Promise.resolve(null)),
    enabled: !!slug && !embedded, // retention adaptation: skip in the chat iframe
  });

  const role = me?.org.role ?? "viewer";
  const connectedPlatforms = new Set<ChannelPlatform>(
    (brand?.connected_accounts ?? []).map((a) => a.platform),
  );
  // SMS-section visibility keys off whether the brand actually *sends* SMS
  // (provider-agnostic), distinct from the connected-platform `channels` gating.
  const brandSendsSms = (brand?.message_channels ?? []).includes("sms");
  // TikTok Shop nav group shows only when the brand has a connected shop.
  const brandTikTokShop = brand?.tiktok_shop_connected ?? false;

  const { region, setRegion, activeRegions, multiRegion } = useRegion(brand ?? null);
  const navigate = useNavigate();
  const location = useLocation();

  // Embedded-chat entity-link receiver — ported 1:1 from ads-iq AppShell.tsx.
  // The FloatingChat iframe posts `{source:"marketplacesiq", type:"open-entity",
  // url}` when an operator clicks a flow/campaign/segment name in chat (see
  // useEntityLinker `open`). The HOST shell — NOT the embedded iframe instance —
  // drives the navigation so the drill opens on the page BEHIND the chat.
  // Gated `if (embedded) return;` so only the top/host AppShell listens (the
  // in-iframe AppShell instance never registers a listener → no double-handling
  // or loops). Same-origin + source + type + url-prefix guarded, matching ads-iq.
  // retention adaptation: `source` is "marketplacesiq" (this app's namespace), not
  // ads-iq's "adsiq"; `type`/`url` and the `/brand/` prefix check are identical.
  useEffect(() => {
    if (embedded) return;
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { source?: string; type?: string; url?: string } | null;
      if (!d || d.source !== "marketplacesiq" || d.type !== "open-entity") return;
      if (typeof d.url !== "string" || !d.url.startsWith("/brand/")) return;
      navigate(d.url);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedded, navigate]);

  // Embedded mode = this AppShell instance is running INSIDE the FloatingChat
  // iframe (/brand/:slug/chat?embedded=1). Mirrors ads-iq AppShell.tsx: the
  // embedded branch renders just the chat (no sidebar/topbar/banners and no
  // recursive ChatPanel) so the chat fills the iframe instead of being wrapped
  // in another full shell. Computed once near the top of the component (it now
  // also gates the shell-only brand/setup queries) — see definition above.

  // Suppress the FloatingChat widget on the dedicated full-page chat route —
  // the full page already owns the screen there (mirrors ads-iq's
  // `!location.pathname.endsWith("/chat")` guard).
  const onChatPage = location.pathname.endsWith("/chat");

  // "All markets" IS the Overview route. The picker shows "All" only while on
  // /overview; everywhere else it shows the active single market (primary by
  // default). Selecting "All" routes to the Overview; selecting a market from
  // the Overview drops into that market's dashboard.
  const onOverview = !!slug && location.pathname.startsWith(`/brand/${slug}/overview`);
  const pickerRegion = onOverview ? null : region;
  function handleRegionChange(next: string | null) {
    if (!next) {
      if (slug) navigate(`/brand/${slug}/overview`);
      return;
    }
    if (onOverview && slug) {
      navigate(`/brand/${slug}/dashboard?region=${encodeURIComponent(next)}`);
    } else {
      setRegion(next);
    }
  }

  function navVisible(item: NavItem): boolean {
    if (item.hidden) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.channels && !item.channels.some((c) => connectedPlatforms.has(c))) return false;
    if (item.minRole && ROLE_RANK[role] < ROLE_RANK[item.minRole]) return false;
    return true;
  }

  // Embedded short-circuit — render only the page body (the iframed Chat),
  // no sidebar/topbar/banners and no recursive ChatPanel. Mirrors ads-iq's
  // embedded branch. UpdateNotice stays mounted so the iframe still polls
  // /api/version and can prompt a refresh on a stale bundle.
  if (embedded) {
    return (
      <main className="h-screen w-screen overflow-hidden p-3">
        <UpdateNotice />
        <Outlet />
      </main>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-bg flex">
      {/* Sidebar — fixed full-height column; only its <nav> scrolls, so the
          sidebar stays put while the main content area scrolls independently. */}
      <aside className="w-60 shrink-0 h-screen bg-bg-card border-r border-border flex flex-col">
        <div className="px-4 py-3.5 border-b border-border-lighter">
          <Link to="/dashboard" className="block">
            <Wordmark size="md" />
          </Link>
          {slug && me && me.brands.length > 1 ? (
            <div className="mt-2">
              <BrandSelector />
            </div>
          ) : (
            brand && (
              <div className="mt-2 flex items-center gap-2">
                <BrandAvatar name={brand.name} slug={brand.slug} size="sm" />
                <div className="text-xs text-text-secondary truncate">{brand.name}</div>
              </div>
            )
          )}
          {slug && multiRegion && (
            <div className="mt-2">
              <RegionPicker
                region={pickerRegion}
                options={activeRegions}
                onChange={handleRegionChange}
              />
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {!slug && (
            <div className="space-y-0.5">
              {/* Targets /portfolio, not /dashboard: those are two different
                  pages here (App.tsx mounts both) and this entry is labelled
                  Portfolio, so it pointed at the brand-cards Dashboard while
                  claiming to be the org-level Portfolio — and never lit up as
                  active on the page it names. */}
              <NavLink to="/portfolio" className={({ isActive }) => linkClasses(isActive)}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
                    )}
                    <IconBadge icon={Home} tint="scale" />
                    Portfolio
                  </>
                )}
              </NavLink>
              {/* Org settings + Admin console moved to the always-visible
                  account area at the sidebar footer (ads-iq parity). */}
            </div>
          )}

          {slug && (
            <Link
              to="/portfolio"
              className="flex items-center gap-2 px-4 py-1.5 mb-2 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover"
            >
              <ArrowLeft size={12} strokeWidth={2} />
              All brands
            </Link>
          )}

          {/* Executive Overview — top brand-nav entry, multi-region only.
              The cross-market landing page; single-region brands never see it
              (they land on Dashboard). */}
          {slug && multiRegion && (
            <div className="space-y-0.5 mb-3">
              <NavLink
                to={`/brand/${slug}/overview`}
                end
                className={({ isActive }) => linkClasses(isActive)}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-accent rounded-r" />
                    )}
                    <IconBadge icon={Globe} tint="warm" />
                    All Markets
                  </>
                )}
              </NavLink>
            </div>
          )}

          {slug &&
            BRAND_NAV.map((group) => {
              if (group.requiresSms && !brandSendsSms) return null;
              if (group.requiresTikTokShop && !brandTikTokShop) return null;
              const visibleItems = group.items.filter(navVisible);
              if (visibleItems.length === 0) return null;
              return (
                <BrandNavSection
                  key={group.id}
                  group={{ ...group, items: visibleItems }}
                  slug={slug}
                />
              );
            })}
        </nav>

        {/* Account area (ads-iq parity): gated admin links → org switcher →
            user identity + sign out. */}
        {(isOrgAdmin || isSuperAdmin) && (
          <div className="border-t border-border-lighter p-2 space-y-0.5">
            {isOrgAdmin && (
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-sm font-medium transition-colors",
                    isActive
                      ? "bg-bg-subtle text-text-primary"
                      : "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
                  )
                }
              >
                <Settings size={15} strokeWidth={1.75} />
                Org settings
              </NavLink>
            )}
            {isSuperAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-sm font-medium transition-colors",
                    isActive
                      ? "bg-amber-50 text-amber-900"
                      : "text-amber-700 hover:bg-amber-50 hover:text-amber-900",
                  )
                }
              >
                <ShieldAlert size={15} strokeWidth={1.75} />
                Admin console
              </NavLink>
            )}
          </div>
        )}

        <div className="border-t border-border-lighter p-2">
          <OrgPicker />
        </div>

        {/* User identity + sign out */}
        <div className="border-t border-border-lighter p-3 flex items-center gap-2">
          <div className="size-8 rounded-full bg-bg-subtle flex items-center justify-center text-sm font-semibold text-text-secondary shrink-0">
            {me?.user.email?.[0]?.toUpperCase() ?? "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-text-primary truncate">
              {me?.user.email ?? "—"}
            </div>
            {me?.org.tier && (
              <div className="text-[11px] text-text-muted capitalize">{me.org.tier}</div>
            )}
          </div>
          <button
            onClick={() => void logout()}
            title="Sign out"
            aria-label="Sign out"
            className="p-1.5 rounded-md text-text-muted hover:bg-bg-hover hover:text-text-primary shrink-0"
          >
            <LogOut size={15} strokeWidth={1.75} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <ImpersonationBanner />
        <Outlet />
      </main>

      {/* Chat advisor — Plan 6. Visible only when slug param present;
          self-renders bottom-right inside the main column. Suppressed on the
          dedicated /chat full-page route (mirrors ads-iq) so the widget and
          the full page don't both render. */}
      {!onChatPage && <ChatPanel />}

      {/* Update notice — modal renders only when version mismatch detected */}
      <UpdateNotice />
    </div>
  );
}
